<script>
  import { onMount, getContext } from "svelte";
  import cloneDeep from "lodash/cloneDeep";
  import set from "lodash/set";
  import get from "lodash/get";
  import ThumbnalView from "./ThumbnailView.svelte";
  import { Modal } from "svelte-utilities";
  import Datatable from "../datatable/Datatable.svelte";
  import SelectInput from "../sidebar/SelectInput.svelte";
  import TextInput from "../sidebar/TextInput.svelte";
  import BooleanInput from "../sidebar/BooleanInput.svelte";
  import { profile } from "#src/stores/auth.js";
  import {
    settingScrollSpeed,
    settingDisplayShowUnusedTypes,
    settingDisplayShowSpanningDims,
    WBPVersion,
    GSVersion,
  } from "#src/stores/ui.js";
  import {
    sortableList,
    addToSortableList,
    removeFromSortableList,
  } from "@local/extensions/collections/sortable-list.js";
  import eb from "#src/extensions/event-bus.js";
  import PlusIcon from "@local/assets/icons/plus.svg";
  import { standardCols } from "@local/lamina-core";
  import { UX_MODE } from "#src/env.js";

  export let group;
  export let standardColumns;
  export let orgColumns;
  export let customColumns;
  export let customColColumns;
  export let disabled = false;

  const org = getContext("org");

  let settingsModal;
  let tab = disabled ? "user_settings" : "job_settings";
  let newOrgColumn = null;

  let prompts = {};

  const standardColumnProps = [
    {
      label: "Name",
      prop: "label",
      editable: false,
    },
    {
      label: "Name override",
      prop: "override",
      editable: true,
    },
    {
      label: "Visible",
      prop: "visible",
      editable: true,
      type: "boolean",
    },
  ];

  const orgColProps = [
    {
      label: "Name",
      prop: "name",
      editable: true,
      type: "text",
    },
  ];

  $: canAdmin = $profile?.profile_capabilities?.capabilities?.edit_basic_org_settings;
  $: sc = getStandardColumns(standardColumns, $org);
  $: labelOptions = makeLabelOptions(customColumns, sc);
  $: units = getUnits($group.data.settings);

  function getUnits(settings) {
    if (settings.display_unit === "inches") {
      if (settings.dimension_format === "DECIMAL") return "inches_decimal";
      if (settings.dimension_format === "FRACTIONAL") return "inches_fractional";
      if (settings.dimension_format === "ARCHITECTURAL") return "inches_architectural";
    } else if (settings.display_unit === "millimeters") {
      return "millimeters_decimal";
    }
  }

  function openSettings() {
    if (disabled) tab = "user_settings";
    settingsModal.open();
  }

  function makeLabelOptions(customColumns, sc) {
    const standard = sc
      .filter((c) => ["mark", "description", "seller_reference", "buyer_reference"].includes(c.id))
      .map((c) => ({
        label: c.override || c.label,
        value: c.id,
      }));

    return [
      ...standard,
      ...customColumns.map((c) => ({
        label: c.name,
        value: `custom_column_data.${c.id}`,
      })),
    ];
  }

  function updateJobSetting(setting, value) {
    group.updateProp(`data.settings.${setting}`, value);
  }

  function updateUnits(value) {
    const settings = cloneDeep($group.data.settings);
    if (value === "inches_decimal") {
      settings.display_unit = "inches";
      settings.dimension_format = "DECIMAL";
    } else if (value === "inches_fractional") {
      settings.display_unit = "inches";
      settings.dimension_format = "FRACTIONAL";
    } else if (value === "inches_architectural") {
      settings.display_unit = "inches";
      settings.dimension_format = "ARCHITECTURAL";
    } else if (value === "millimeters_decimal") {
      settings.display_unit = "millimeters";
      settings.dimension_format = "DECIMAL";
    }
    group.updateProp("data.settings", settings);
  }

  function updateHiddenColumns(prop, value) {
    const data = cloneDeep($group.data);
    data.settings.output_hidden_columns = {
      ...data.settings.output_hidden_columns,
      [prop]: value,
    };
    group.updateProp("data", data);
  }

  function getStandardColumns(standardColumns, org) {
    const hasTemplates = !!org?.data?.templates?.length;

    return standardCols(hasTemplates).map((c) => {
      const visible = ["x_subdivisions", "y_subdivisions"].includes(c.id) ? false : true; // Hide subdivisions by default

      return (
        standardColumns[c.id] || {
          ...c,
          override: null,
          visible,
        }
      );
    });
  }

  function updateStandardColumn(e) {
    const { id, prop, value } = e.detail;
    const data = cloneDeep($org.data);
    const newSc = sortableList(sc);
    set(newSc[id], prop, value);
    data.standard_columns = newSc;
    org.updateProp("data", data);
  }

  function beginAddingOrgColumn() {
    newOrgColumn = { name: "New Column", type: "text" };
  }

  function addNewOrgColumn(evt) {
    const { row } = evt.detail;

    const data = cloneDeep($org.data);
    const cc = get(data, "custom_columns") || sortableList([]);

    addToSortableList(cc, {
      id: crypto.randomUUID(),
      ...row,
    });

    data.custom_columns = cc;
    org.updateProp("data", data);
    newOrgColumn = null;
  }

  function removeOrgColumn(evt) {
    const { id } = evt.detail;
    const data = cloneDeep($org.data);
    const deletedCols = data.deleted_columns || {};
    deletedCols[id] = data.custom_columns[id];
    removeFromSortableList(data.custom_columns, id);
    data.deleted_columns = deletedCols;

    org.updateProp("data", data);
  }

  function stopAddingOrgColumn() {
    newOrgColumn = null;
  }

  function updateOrgColumn(e) {
    const { id, prop, value } = e.detail;
    const data = cloneDeep($org.data);
    const cc = get(data, "custom_columns") || sortableList([]);
    set(cc[id], prop, value);
    data.custom_columns = cc;
    org.updateProp("data", data);
  }

  function numParser(v) {
    const result = parseFloat(v);
    if (isNaN(result)) throw new Error("Not parseable as a number");
    if (result <= 0) throw new Error("Value must be positive");
    return result;
  }

  function opacityParser(v) {
    const result = parseFloat(v);
    if (isNaN(result)) throw new Error("Not parseable as a number");
    if (result < 0 || result > 1) throw new Error("Value must be between 0 and 1");
    return result;
  }

  onMount(() => {
    eb.on("open-settings", openSettings);

    return () => {
      eb.unsubscribe("open-settings", openSettings);
    };
  });
</script>

<Modal bind:this={settingsModal} closeable fullframe width="36rem" height="26rem">
  <div slot="title">Settings</div>
  <div slot="content" class="text-xs flex h-full">
    <div class="flex-none w-36 bg-gray-100 p-2 space-y-1">
      {#if !disabled}
        <button
          class="setting-tab"
          class:selected={tab === "job_settings"}
          on:click={() => (tab = "job_settings")}>
          Job Settings
        </button>
      {/if}
      <button
        class="setting-tab"
        class:selected={tab === "user_settings"}
        on:click={() => (tab = "user_settings")}>
        User Settings
      </button>
      <button
        class="setting-tab"
        class:selected={tab === "org_settings"}
        on:click={() => (tab = "org_settings")}>
        Org. Settings
      </button>
      {#if $profile?.profile_capabilities?.capabilities?.switch_organization}
        <button
          class="setting-tab"
          class:selected={tab === "developer_settings"}
          on:click={() => (tab = "developer_settings")}>
          Developer Settings
        </button>
      {/if}
      <!-- <button
        class="setting-tab"
        class:selected={tab === "output_doc_settings"}
        on:click={() => (tab = "output_doc_settings")}>
        Report Settings
      </button> -->
    </div>
    <div class="grow flex flex-col space-y-2 p-4 h-full overflow-auto">
      {#if tab === "default_job_settings"}
        <div class="flex items-center justify-between" />
      {:else if tab === "job_settings"}
        <div class="flex items-center justify-between">
          <div>Display unit:</div>
          <div class="flex-none w-60">
            <SelectInput
              options={[
                { label: "Inches, sf (Fractional)", example: '14 1/4"', value: "inches_fractional" },
                { label: "Inches, sf (Architectural)", example: "1'-2 1/4\"", value: "inches_architectural" },
                { label: "Inches, sf (Decimal)", example: '14.25"', value: "inches_decimal" },
                { label: "Millimeters, m² (Decimal)", example: "362mm", value: "millimeters_decimal" },
              ]}
              value={units}
              label={null}
              on:input={(e) => updateUnits(e.detail.value)}>
              <div slot="option" let:option class="flex items-center gap-1 justify-between w-full">
                <div>{option.label}</div>
                <div class="text-gray-500 italic text-xs">{option.example}</div>
              </div>
            </SelectInput>
          </div>
        </div>
        {#if $group.data.settings.display_unit === "inches"}
          {#if $group.data.settings.dimension_format === "DECIMAL"}
            <div class="flex items-center justify-between">
              <div>Display precision:</div>
              <div class="flex-none w-60">
                <SelectInput
                  options={[
                    { label: "0", value: 0 },
                    { label: "1", value: 1 },
                    { label: "2", value: 2 },
                    { label: "3", value: 3 },
                    { label: "4", value: 4 },
                  ]}
                  value={$group.data.settings.decimal_precision}
                  label={null}
                  on:input={(e) => updateJobSetting("decimal_precision", e.detail.value)} />
              </div>
            </div>
            <div class="flex items-center justify-between">
              <div>Doc dimension precision:</div>
              <div class="flex-none w-60">
                <SelectInput
                  options={[
                    { label: "0", value: 0 },
                    { label: "1", value: 1 },
                    { label: "2", value: 2 },
                    { label: "3", value: 3 },
                    { label: "4", value: 4 },
                  ]}
                  value={$group.data.settings.dim_decimal_precision}
                  label={null}
                  on:input={(e) => updateJobSetting("dim_decimal_precision", e.detail.value)} />
              </div>
            </div>
          {:else if $group.data.settings.dimension_format === "FRACTIONAL" || $group.data.settings.dimension_format === "ARCHITECTURAL"}
            <div class="flex items-center justify-between">
              <div>Display precision:</div>
              <div class="flex-none w-60">
                <SelectInput
                  options={[
                    { label: '1"', value: 0 },
                    { label: '1/2"', value: 1 },
                    { label: '1/4"', value: 2 },
                    { label: '1/8"', value: 3 },
                    { label: '1/16"', value: 4 },
                    { label: '1/32"', value: 5 },
                  ]}
                  value={$group.data.settings.fractional_precision}
                  label={null}
                  on:input={(e) => updateJobSetting("fractional_precision", e.detail.value)} />
              </div>
            </div>
            <div class="flex items-center justify-between">
              <div>Doc dimension precision:</div>
              <div class="flex-none w-60">
                <SelectInput
                  options={[
                    { label: '1"', value: 0 },
                    { label: '1/2"', value: 1 },
                    { label: '1/4"', value: 2 },
                    { label: '1/8"', value: 3 },
                    { label: '1/16"', value: 4 },
                    { label: '1/32"', value: 5 },
                  ]}
                  value={$group.data.settings.dim_fractional_precision}
                  label={null}
                  on:input={(e) => updateJobSetting("dim_fractional_precision", e.detail.value)} />
              </div>
            </div>
          {/if}
        {:else}
          <div class="flex items-center justify-between">
            <div>Display precision:</div>
            <div class="flex-none w-60">
              <SelectInput
                options={[
                  { label: "0", value: 0 },
                  { label: "1", value: 1 },
                  { label: "2", value: 2 },
                ]}
                value={$group.data.settings.mm_precision}
                label={null}
                on:input={(e) => updateJobSetting("mm_precision", e.detail.value)} />
            </div>
          </div>
          <div class="flex items-center justify-between">
            <div>Doc dimension precision:</div>
            <div class="flex-none w-60">
              <SelectInput
                options={[
                  { label: "0", value: 0 },
                  { label: "1", value: 1 },
                  { label: "2", value: 2 },
                ]}
                value={$group.data.settings.dim_mm_precision}
                label={null}
                on:input={(e) => updateJobSetting("dim_mm_precision", e.detail.value)} />
            </div>
          </div>
        {/if}

        <div class="flex items-center justify-between">
          <div>Printed marker size:</div>
          <div class="flex-none w-60">
            <TextInput
              label={null}
              labelWidth="7.5rem"
              value={$group.data.settings.print_location_size}
              postlabel="pts"
              parser={numParser}
              on:input={(e) => updateJobSetting("print_location_size", e.detail.value)} />
          </div>
        </div>

        <div class="flex items-center justify-between">
          <div>Label items by:</div>
          <div class="flex-none w-60">
            <SelectInput
              options={labelOptions}
              value={$group.data.settings.label_by}
              label={null}
              on:input={(e) => updateJobSetting("label_by", e.detail.value)} />
          </div>
        </div>

        <div class="flex items-center justify-between">
          <div>Label openings by:</div>
          <div class="flex-none w-60">
            <SelectInput
              options={labelOptions}
              value={$group.data.settings.collections_label_by}
              label={null}
              on:input={(e) => updateJobSetting("collections_label_by", e.detail.value)} />
          </div>
        </div>

        <div class="flex items-center justify-between">
          <div>Show Width Offset</div>
          <div class="flex-none w-60">
            <BooleanInput
              value={!$group.data.settings.hide_width_offset}
              label={null}
              on:input={(e) => updateJobSetting("hide_width_offset", !e.detail.value)} />
          </div>
        </div>

        <div class="flex items-center justify-between">
          <div>Show Height Offset</div>
          <div class="flex-none w-60">
            <BooleanInput
              value={!$group.data.settings.hide_height_offset}
              label={null}
              on:input={(e) => updateJobSetting("hide_height_offset", !e.detail.value)} />
          </div>
        </div>

        <div class="flex items-center justify-between">
          <div>Underlay opacity:</div>
          <div class="flex-none w-60">
            <TextInput
              label={null}
              labelWidth="7.5rem"
              value={$group.data.settings.underlay_opacity}
              parser={opacityParser}
              on:input={(e) => updateJobSetting("underlay_opacity", e.detail.value)} />
          </div>
        </div>
      {:else if tab === "user_settings"}
        <div class="flex items-center justify-between">
          <div>Scroll speed:</div>
          <div class="flex-none w-56 px-2">
            <SelectInput
              options={[
                { label: "Slow", value: -0.6 },
                { label: "Medium-slow", value: -0.3 },
                { label: "Medium", value: 0 },
                { label: "Medium-fast", value: 0.3 },
                { label: "Fast", value: 0.6 },
              ]}
              label={null}
              bind:value={$settingScrollSpeed} />
          </div>
        </div>

        {#if UX_MODE !== "shpx"}
          <div class="flex items-center justify-between">
            <div>Show unused types:</div>
            <div class="flex-none w-56 px-4 py-2">
              <input type="checkbox" bind:checked={$settingDisplayShowUnusedTypes} />
            </div>
          </div>

          <div class="flex items-center justify-between">
            <div>Show redundant dims:</div>
            <div class="flex-none w-56 px-4 py-2">
              <input type="checkbox" bind:checked={$settingDisplayShowSpanningDims} />
            </div>
          </div>

          <div class="pt-2">
            <div>Thumbnail display:</div>
            <div class="flex justify-center">
              <ThumbnalView />
            </div>
          </div>
        {/if}
      {:else if tab === "org_settings"}
        {#if canAdmin}
          <div class="italic">Changes to these settings will affect all jobs.</div>
        {/if}
        <div>
          <div class="mb-2">Standard Columns</div>
          <div class="data-table-container">
            <Datatable
              sticky
              size="xxs"
              data={sc}
              columns={standardColumnProps}
              editable={canAdmin}
              on:updaterow={updateStandardColumn} />
          </div>
        </div>
        <div>
          <div class="mb-2 flex items-center">
            <div class="grow">Organization Columns</div>
            {#if canAdmin}
              <button class="w-8 flex-none" on:click={beginAddingOrgColumn}>
                <PlusIcon />
              </button>
            {/if}
          </div>
          <div class="data-table-container">
            <Datatable
              sticky
              size="xxs"
              data={orgColumns}
              columns={orgColProps}
              newRow={newOrgColumn}
              deletable
              editable={canAdmin}
              on:addrow={addNewOrgColumn}
              on:deleterow={removeOrgColumn}
              on:updaterow={updateStandardColumn}
              on:stopaddingrow={stopAddingOrgColumn}
              on:updaterow={updateOrgColumn} />
          </div>
        </div>
      {:else if tab === "developer_settings"}
        <div class="flex items-center justify-between">
          <div>WinBidPro Output Version:</div>
          <div class="flex-none w-56">
            <SelectInput
              options={[
                { label: "1.0.0", value: "1.0.0" },
                { label: "1.1.0", value: "1.1.0" },
              ]}
              bind:value={$WBPVersion}
              label={null} />
          </div>
        </div>
        <div class="flex items-center justify-between">
          <div>GlazierStudio Output Version:</div>
          <div class="flex-none w-56">
            <SelectInput
              options={[
                { label: "1.0.0", value: "1.0.0" },
                { label: "1.1.0", value: "1.1.0" },
              ]}
              bind:value={$GSVersion}
              label={null} />
          </div>
        </div>
      {/if}
    </div>
  </div>
</Modal>

<style lang="scss">
  .setting-tab {
    @apply p-2 cursor-pointer w-full text-left;

    &:hover,
    &.selected {
      @apply bg-gray-300 rounded;
    }
  }

  .data-table-container {
    @apply border overflow-y-auto relative;
    max-height: 9rem;
  }
</style>
